import React from 'react';
import { ArrowDown, RotateCcw, FileText, FileSpreadsheet, FileDown, Play, Ban, Lock } from 'lucide-react';

/**
 * Report toolbar — 4 sub-sections distributed evenly horizontally:
 * 1. Run Report   2. Sorting Parameters   3. Report Details   4. Export Options
 */
const ReportToolbar = ({
  onRunReport, loading, activeFilterCount, onClearFilters,
  isDefaultSort, sortOrder, defaultSort, onResetSort,
  totalCount, executionMs,
  pageSize, pageSizeLabel, totalPages,
  hasRun, hasOverLimit = false, isCurrent = false,
  onExportPdf = null, onExportExcel = null, onExportCsv = null,
  exportingType = null, exportProgress = 0, onCancelExport = null,
  columns = [], exportGroupBy = null, onExportGroupByChange = null,
  exportGroupSeparate = false, onExportGroupSeparateChange = null,
  exportGroupCount = 0,
  exportGroupByLabel = null
}) => {
  const canRun = !loading && !hasOverLimit;
  const runLabel = loading ? 'Running...' : isCurrent ? 'Refresh' : 'Run Report';

  // Row-count-based export time estimates derived from observed benchmarks:
  // PDF ~65s for 3000 rows (~0.022s/row), Excel ~0.005s/row, CSV ~0.001s/row
  const fmtTime = (seconds) => {
    const total = Math.ceil(seconds);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `~${m}:${String(s).padStart(2, '0')}`;
  };
  const rows = hasRun ? (totalCount || 0) : 0;
  const estPdf   = rows > 0 ? fmtTime(Math.max(5,  rows * 0.022)) : '—';
  const estExcel = rows > 0 ? fmtTime(Math.max(2,  rows * 0.005)) : '—';
  const estCsv   = rows > 0 ? fmtTime(Math.max(1,  rows * 0.001)) : '—';

  const subStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '14px 20px',
    gap: '10px',
    flex: 1,
    minWidth: 'max-content',
  };

  const labelStyle = {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--color-gray-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'space-evenly', alignItems: 'stretch', flexWrap: 'wrap' }}>
      <style>{`
        .toolbar-subsection + .toolbar-subsection {
          border-left: 1px solid var(--color-gray-200);
        }
        @media (max-width: 900px) {
          .toolbar-subsection + .toolbar-subsection {
            border-left: none;
            border-top: 1px solid var(--color-gray-200);
          }
        }
      `}</style>

      {/* ── Sub-section 1: Run Report ── */}
      <div className="toolbar-subsection" style={subStyle}>
        <div style={labelStyle}>Run Report</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            className="sync-acknowledge-btn"
            onClick={() => onRunReport()}
            disabled={!canRun}
            style={!canRun ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
            title={
              hasOverLimit
                ? 'Reduce parameter selections to 4 or fewer before running'
                : activeFilterCount === 0
                  ? 'Select at least one parameter before running'
                  : undefined
            }
          >
            <Play size={16} /> {runLabel}
          </button>
        </div>
      </div>

      {/* ── Sub-section 2: Sorting Parameters ── */}
      <div className="toolbar-subsection" style={subStyle}>
        <div style={labelStyle}>Sorting Parameters</div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          {sortOrder.length > 0 ? (
            sortOrder.map((s, i) => (
              <span key={s.field}
                title={`Sorted ${s.direction === 'asc' ? 'ascending' : 'descending'} by ${s.field} (priority ${i + 1})`}
                style={{
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                padding: '1px 6px', borderRadius: '8px', fontSize: '11px',
                background: 'var(--color-primary-light)', color: 'var(--color-primary)',
                whiteSpace: 'nowrap'
              }}>
                <span style={{
                  fontSize: '9px', fontWeight: 700,
                  background: 'var(--color-primary)', color: '#fff',
                  borderRadius: '50%', width: '14px', height: '14px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {i + 1}
                </span>
                {s.field} {s.direction === 'asc' ? '\u2191' : '\u2193'}
              </span>
            ))
          ) : (
            <span style={{ fontSize: '11px', color: 'var(--color-gray-400)', fontStyle: 'italic', whiteSpace: 'nowrap' }}>No sort applied</span>
          )}
          <button
            onClick={isDefaultSort ? undefined : onResetSort}
            title={
              isDefaultSort
                ? `Default sort: ${defaultSort.map(s => `${s.field} ${s.direction}`).join(', ')}`
                : 'Click to reset to default sort'
            }
            style={{
              display: 'flex', alignItems: 'center', gap: '3px',
              padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
              border: `1px solid ${isDefaultSort ? 'var(--color-primary-light)' : '#ffc107'}`,
              background: isDefaultSort ? 'var(--color-primary-light)' : '#fff8e1',
              color: isDefaultSort ? 'var(--color-primary)' : '#856404',
              cursor: isDefaultSort ? 'default' : 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {isDefaultSort ? <><ArrowDown size={10} /> Default</> : <><RotateCcw size={10} /> Reset</>}
          </button>
        </div>
      </div>

      {/* ── Sub-section 3: Report Details ── */}
      <div className="toolbar-subsection" style={subStyle}>
        <div style={labelStyle}>Report Details</div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', justifyContent: 'center' }}>
          <Stat label="Rows"      value={hasRun ? totalCount.toLocaleString() : '—'} />
          <Stat label="Pages"     value={hasRun ? totalPages : '—'} />
          <Stat label="Rows/Page" value={hasRun ? pageSize : '—'} />
          <Stat label="Layout"    value={hasRun ? pageSizeLabel : '—'} />
          <Stat label="Time"      value={hasRun && executionMs != null ? `${executionMs}ms` : '—'} />
        </div>
      </div>

      {/* ── Sub-section 4: Grouping ── */}
      {columns.length > 0 && (onExportGroupByChange || exportGroupBy) && (
        <div className="toolbar-subsection" style={subStyle}>
          <div style={labelStyle}>Grouping</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '11px' }}>
            <span style={{ color: 'var(--color-gray-500)', fontWeight: 600, whiteSpace: 'nowrap' }}>Group By:</span>
            {onExportGroupByChange ? (
              <select
                value={exportGroupBy || ''}
                onChange={e => onExportGroupByChange(e.target.value || null)}
                title="Group the report preview and export output by unique values of this column"
                style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '3px', border: '1px solid var(--color-gray-300)', color: 'var(--color-gray-700)', background: '#fff' }}
              >
                <option value="">-- None --</option>
                {columns.map(col => <option key={col.field} value={col.field}>{col.label}</option>)}
              </select>
            ) : (
              <span
                title="Grouping is fixed for this report and cannot be changed"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                  background: 'var(--color-primary-light)', color: 'var(--color-primary)',
                  border: '1px solid var(--color-primary-light)',
                  whiteSpace: 'nowrap'
                }}
              >
                <Lock size={10} /> {exportGroupByLabel || exportGroupBy}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Sub-section 5: Export Options ── */}
      <div className="toolbar-subsection" style={subStyle}>
        <div style={labelStyle}>Export Options</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', fontSize: '11px' }}>
            <label
              title={exportGroupBy ? 'Export each group as a separate file (auto-download)' : 'Select a grouping parameter to enable separate file export'}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: exportGroupBy ? 'pointer' : 'not-allowed', color: exportGroupBy ? 'var(--color-gray-600)' : 'var(--color-gray-400)', userSelect: 'none', whiteSpace: 'nowrap', opacity: exportGroupBy ? 1 : 0.5 }}
            >
              <input
                type="checkbox"
                checked={exportGroupSeparate}
                disabled={!exportGroupBy}
                onChange={e => onExportGroupSeparateChange && onExportGroupSeparateChange(e.target.checked)}
              />
              Separate Files
              {exportGroupBy && exportGroupCount > 0 && (
                <span style={{ color: 'var(--color-gray-400)', fontSize: '10px' }}>({exportGroupCount} files)</span>
              )}
            </label>
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', justifyContent: 'center' }}>
          <ExportBtn icon={FileText}        label="PDF"   type="pdf"   exportingType={exportingType} exportProgress={exportProgress} onClick={onExportPdf}   estTime={estPdf} />
          <ExportBtn icon={FileSpreadsheet} label="Excel" type="excel" exportingType={exportingType} exportProgress={exportProgress} onClick={onExportExcel} estTime={estExcel} />
          <ExportBtn icon={FileDown}        label="CSV"   type="csv"   exportingType={exportingType} exportProgress={exportProgress} onClick={onExportCsv}   estTime={estCsv} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={exportingType ? onCancelExport : undefined}
              disabled={!exportingType}
              title={exportingType ? 'Cancel the current export' : 'No export in progress'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
                width: '110px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px',
                border: `1px solid ${exportingType ? '#dc2626' : 'var(--color-gray-300)'}`,
                background: exportingType ? '#fff5f5' : 'var(--color-gray-100)',
                color: exportingType ? '#dc2626' : 'var(--color-gray-400)',
                cursor: exportingType ? 'pointer' : 'not-allowed',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              <Ban size={12} /> Cancel Export
            </button>
            <span style={{ fontSize: '9px', color: 'transparent' }}>—</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stat label/value pair
const Stat = ({ label, value }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '9px', color: 'var(--color-gray-400)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
      {label}
    </div>
    <div style={{ fontSize: '12px', color: 'var(--color-gray-700)', fontWeight: 600 }}>
      {value}
    </div>
  </div>
);

// Export button with estimated time below
const ExportBtn = ({ icon: Icon, label, type, exportingType, exportProgress, onClick, estTime }) => {
  const isActive   = exportingType === type;
  const isBusy     = exportingType !== null;
  const isDisabled = isBusy || !onClick;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
      {isActive ? (
        <button
          disabled
          title={`Generating ${label}...`}
          style={{
            position: 'relative', overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
            width: '110px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px',
            border: '1px solid var(--color-primary)',
            background: 'var(--color-gray-100)',
            color: 'var(--color-primary)',
            fontWeight: 600, cursor: 'wait', whiteSpace: 'nowrap'
          }}
        >
          <div style={{
            position: 'absolute', top: 0, left: 0, height: '100%',
            width: `${Math.max(4, exportProgress)}%`,
            background: 'linear-gradient(to right, rgba(34,197,94,0.45), rgba(34,197,94,0.1))',
            transition: 'width 1s linear',
            borderRadius: '3px'
          }} />
          <span style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: '3px' }}>
            <Icon size={12} /> Generating...
          </span>
        </button>
      ) : (
        <button
          onClick={isDisabled ? undefined : onClick}
          disabled={isDisabled}
          title={`Export ${label}`}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
            width: '110px', padding: '6px 10px', borderRadius: '4px', fontSize: '11px',
            border: '1px solid var(--color-gray-300)',
            background: isDisabled ? 'var(--color-gray-100)' : '#fff',
            color: isDisabled ? 'var(--color-gray-400)' : 'var(--color-gray-700)',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isBusy ? 0.5 : 1
          }}
        >
          <Icon size={12} /> {label}
        </button>
      )}
      <span style={{ fontSize: '9px', color: 'var(--color-gray-400)' }}>{estTime}</span>
    </div>
  );
};

export default ReportToolbar;
